import React from 'react'
import styled from 'styled-components'

interface ToggleSwitchProps {
  isChecked: boolean
  onChange: (checked: boolean) => void
  leftLabel?: string
  rightLabel?: string
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  isChecked,
  onChange,
  leftLabel = 'Active',
  rightLabel = 'Archived',
  disabled = false,
  size = 'medium',
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange(!isChecked)
    }
  }

  return (
    <ToggleContainer size={size}>
      <Label $active={!isChecked} onClick={handleToggle}>
        {leftLabel}
      </Label>
      <SwitchContainer $disabled={disabled} onClick={handleToggle}>
        <SwitchTrack $checked={isChecked} size={size}>
          <SwitchThumb $checked={isChecked} size={size} />
        </SwitchTrack>
      </SwitchContainer>
      <Label $active={isChecked} onClick={handleToggle}>
        {rightLabel}
      </Label>
    </ToggleContainer>
  )
}

const ToggleContainer = styled.div<{ size: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme, size }) => {
    switch (size) {
      case 'small':
        return theme.spacing.xs
      case 'large':
        return theme.spacing.s
      default:
        return theme.spacing.xxs
    }
  }};
  user-select: none;
`

const Label = styled.span<{ $active: boolean }>`
  ${({ theme }) => theme.typography.s.normal};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary.neutral : theme.colors.grey.neutral)};
  font-weight: ${({ theme, $active }) => ($active ? theme.fontWeight.bold : theme.fontWeight.normal)};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.primary.neutral};
  }
`

const SwitchContainer = styled.div<{ $disabled: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`

const SwitchTrack = styled.div<{ $checked: boolean; size: string }>`
  position: relative;
  width: ${({ size }) => {
    switch (size) {
      case 'small':
        return '32px'
      case 'large':
        return '48px'
      default:
        return '40px'
    }
  }};
  height: ${({ size }) => {
    switch (size) {
      case 'small':
        return '18px'
      case 'large':
        return '26px'
      default:
        return '22px'
    }
  }};
  background-color: ${({ theme, $checked }) => ($checked ? theme.colors.primary.neutral : theme.colors.grey.lighter)};
  border-radius: ${({ size }) => {
    switch (size) {
      case 'small':
        return '9px'
      case 'large':
        return '13px'
      default:
        return '11px'
    }
  }};
  transition: background-color 0.2s ease-in-out;
  border: 1px solid ${({ theme, $checked }) => ($checked ? theme.colors.primary.neutral : theme.colors.grey.neutral)};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.neutral};
  }
`

const SwitchThumb = styled.div<{ $checked: boolean; size: string }>`
  position: absolute;
  top: 1px;
  left: ${({ $checked, size }) => {
    if ($checked) {
      switch (size) {
        case 'small':
          return '16px'
        case 'large':
          return '24px'
        default:
          return '20px'
      }
    }
    return '1px'
  }};
  width: ${({ size }) => {
    switch (size) {
      case 'small':
        return '14px'
      case 'large':
        return '22px'
      default:
        return '18px'
    }
  }};
  height: ${({ size }) => {
    switch (size) {
      case 'small':
        return '14px'
      case 'large':
        return '22px'
      default:
        return '18px'
    }
  }};
  background-color: ${({ theme }) => theme.colors.neutral.lightest};
  border-radius: 50%;
  transition: left 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`
